import * as React from 'react';
import {useEffect, useState} from 'react';
import clsx from 'clsx';

import {ProductUpdate} from 'app_api/product_updates/types';
import {SelectReact} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {useThemes} from 'common/contexts/react/themes-context';
import {useTranslation} from 'common/i18n/hooks';
import {Permission, ProjectFeatureFlag, ReportName, UNIQUE_PROJECTS, UserFlag} from 'common/report/constants';
import {useLocalStorageManager} from 'common/contexts/react/storage-manager-context';
import {getProductUpdates, markProductUpdatesAsSeen} from 'common/report/queries/app-api/product-updates-client';
import {userCookieUtils, UserIdentity} from 'common/report/util/user-identity-from-cookie';
import {changeLanguage, LOCALES} from 'common/i18n';
import {Directions} from 'common/components/drop-menu/types';
import {Section, SectionItem, SelectedTheme} from 'common/components/items-menu/types';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {useCurrentUser} from 'common/contexts/react/current-user-context';
import {ChangeModalReact} from 'common/widgets/changelog-modal';
import {promptFeedbackModal} from 'common/widgets/feedback-modal';
import {FeedbackForFeature} from 'common/widgets/feedback-modal/types';
import {openDemoProjectSelectorModal} from 'common/widgets/demo-selector-modal';
import {ToastType} from 'common/components/toast/types';
import {getReportName, getReportUrl} from 'common/report/util';
import {withStyles} from 'common/util/react';
import {Step} from 'common/onboarding/immersive-onboarding/types';
import {addToast} from 'common/components/toast-area/toast-manager';
import {Placement} from 'common/components/tooltip/types';
import {useSupportInfo} from 'common/contexts/react/support-info-context';
import {SupportWidgetReact} from 'iron/routes/global-sidenav/widgets/support-widget';
import {useGlobalSidenavController} from 'common/contexts/react/global-sidenav-controller-context';
import {ChangeEvent, MenuChangeEvent} from 'common/components/select/types';
import {
  getSettingsMenuItems,
  getStartupCreditPromoSectionItem,
  getUnseenProductUpdates,
  isProjectOrGlobalWorkspace,
  logout,
  warmUsageAnalyticsData,
} from 'iron/routes/global-sidenav/footer/utils';
import {setUserFlag} from 'common/report/queries/app-api/user-flags';
import {userFlagsStore} from 'common/report/stores/user-flags';
import {LocaleCode} from 'common/i18n/locales';
import {useTours} from 'common/contexts/react/tours-context';
import {FooterMenuItemType, FooterMenuType, TrackingEvent, UrlLocation} from 'iron/routes/global-sidenav/types';
import {IMMERSIVE_ONBOARDING_URL_PARAM} from 'common/onboarding/immersive-onboarding/constants';
import {getFilteredOrgs} from 'iron/routes/global-sidenav/utils';
import {ColorSchemeSetting} from 'common/report/model-resources/themes/types';

import styles from './index.module.styl';
import css from './index.module.styl?inline';
import {FooterAppReportInfo, FooterAppReportName} from './types';

export type Props = {
  location: UrlLocation;
};

function FooterMenu(props: {
  type: FooterMenuType;
  className?: string;
  icon: string;
  sections: Section[];
  onMenuChange?: (ev: MenuChangeEvent) => void;
}) {
  const globalSidenavController = useGlobalSidenavController();
  const i18n = useTranslation();

  const handleMenuChange = (ev: MenuChangeEvent) => {
    if (ev.detail.isOpen) {
      globalSidenavController.mixpanelTrack(TrackingEvent.FooterMenuOpened, {
        'Footer Menu Type': props.type,
      });
    }
    props.onMenuChange?.(ev);
  };

  const handleChange = (ev: ChangeEvent) => {
    const {item, parentItems} = ev.detail;
    let selectedItem = item.value ?? i18n.print(item.label);
    let selectedSubItem: string | undefined = undefined;
    if (parentItems?.length) {
      selectedSubItem = selectedItem;
      selectedItem = parentItems[0].value ?? i18n.print(parentItems[0].label);
    }
    globalSidenavController.mixpanelTrack(TrackingEvent.FooterMenuItemSelected, {
      'Footer Menu Type': props.type,
      'Selected Item': selectedItem,
      ...(selectedSubItem && {'Selected Sub Item': selectedSubItem}),
    });
  };

  return (
    <SelectReact
      className={props.className}
      icon={{leftIcon: props.icon}}
      theme={SelectTheme.Tertiary}
      iconOnly
      neverShowCaret
      experimentalPositioning
      menuHeight={0}
      menuWidth='null'
      menuAlignment={globalSidenavController.isCollapsed() ? Directions.RightBottom : Directions.Up}
      disableSearch
      onMenuChange={handleMenuChange}
      // @ts-ignore
      onChange={handleChange}
      sections={props.sections}
    />
  );
}

function SettingsMenu() {
  const currentProject = useCurrentProject().get().value;
  const currentUser = useCurrentUser().get().value;
  const i18n = useTranslation();
  const themes = useThemes();
  const tours = useTours();
  const globalSidenavController = useGlobalSidenavController();
  const localStorageManager = useLocalStorageManager();

  const handleMenuChange = (ev: MenuChangeEvent) => {
    if (ev.detail.isOpen) {
      warmUsageAnalyticsData({currentProject, currentUser});
    }
  };

  const handleChangeLanguage = (locale: LocaleCode) => {
    if (locale !== LocaleCode.English) {
      tours.openTour(`localization`);
    }
    changeLanguage(locale);
  };

  const isProjectOrGlobalWorkspaceVal = currentProject ? isProjectOrGlobalWorkspace({currentProject}) : undefined;
  const userOrganizations = currentUser?.organizations ?? {};
  const currentOrganization = currentProject ? userOrganizations[currentProject.organizationId] : undefined;
  const showSetUpMixpanel = Boolean(currentProject);
  const showPlanDetails = Boolean(
    currentProject && currentOrganization?.permissions?.includes(Permission.ViewOrganizationSettings),
  );
  const showInviteUsers = Boolean(currentOrganization?.permissions.includes(Permission.AddUser));
  // boolean used for getting users back to FYT (find your team); for FYT users, will only have demo project org available
  const showBackToSignup = Boolean(
    currentUser && getFilteredOrgs(currentUser.organizations, {excludeDemoOrgs: true}).length === 0,
  );

  let startupCreditPromoSectionItem = getStartupCreditPromoSectionItem({currentProject});
  if (startupCreditPromoSectionItem) {
    startupCreditPromoSectionItem = {
      ...startupCreditPromoSectionItem,
      value: FooterMenuItemType.StartupCreditPromo,
      onSelect: () => {
        setUserFlag(UserFlag.ViewedStartupCreditPromo);
        userFlagsStore.update({[UserFlag.ViewedStartupCreditPromo]: 1});
      },
    };
  }

  const adminOrgs = Object.values(currentUser?.organizations ?? {})
    .filter((org) => org.is_actionable_admin_org)
    .map((org) => ({id: org.id.toString(), name: org.name}));

  return (
    <FooterMenu
      type={FooterMenuType.Settings}
      icon={`settings`}
      onMenuChange={handleMenuChange}
      sections={[
        {
          items: [
            {
              label: i18n.t(`nav-header.settings`),
              value: FooterMenuItemType.Settings,
              icon: `brand-settings`,
              subSections: [
                {
                  items: getSettingsMenuItems({currentUser, currentProject}),
                },
              ],
            },
            {
              label: i18n.t(`settings.language`),
              value: FooterMenuItemType.Language,
              icon: `globe`,
              subSections: [
                {
                  selectedTheme: SelectedTheme.Checkmark,
                  items: LOCALES.map((locale) => ({
                    label: locale.displayName,
                    value: locale.code,
                    isSelected: locale.code === i18n.language,
                    onSelect: (item) => handleChangeLanguage(item.value),
                  })),
                },
              ],
            },
            {
              label: i18n.t(`settings.appearance`),
              value: FooterMenuItemType.Appearance,
              icon: `light-mode`,
              subSections: [
                {
                  selectedTheme: SelectedTheme.Checkmark,
                  items: [
                    {
                      label: i18n.t(`common.auto`),
                      value: ColorSchemeSetting.Auto,
                    },
                    {
                      label: i18n.t(`common.dark`),
                      value: ColorSchemeSetting.DarkMode,
                    },
                    {
                      label: i18n.t(`common.light`),
                      value: ColorSchemeSetting.LightMode,
                    },
                  ].map((item) => {
                    return {
                      ...item,
                      onSelect: () => themes.updateColorSchemeSetting(item.value),
                      isSelected: item.value === themes.getColorSchemeSetting(),
                    };
                  }),
                },
              ],
            },
            {
              label: i18n.t(`mixology.app.integrations`),
              value: ReportName.Integrations,
              icon: `brand-integrations`,
              href: globalSidenavController.getWorkspaceBasedAppUrl(`integrations`),
              isDisabled: !isProjectOrGlobalWorkspaceVal,
              ...(!isProjectOrGlobalWorkspaceVal && {
                tooltip: {
                  placement: Placement.Right,
                  text: i18n.t(`nav-header.integrations-disabled`),
                },
              }),
            },
            ...(showSetUpMixpanel
              ? [
                  {
                    label: i18n.t(`settings.setup`),
                    value: FooterMenuItemType.ImplementMixpanel,
                    href: `?${IMMERSIVE_ONBOARDING_URL_PARAM}=${Step.Implement}`,
                    icon: `mixpanel-logo`,
                  },
                ]
              : []),
            ...(showPlanDetails
              ? [
                  {
                    label: i18n.t(`settings.billing-plan`),
                    value: FooterMenuItemType.BillingPlan,
                    href: getReportUrl(`settings/#org/${currentOrganization!.id}/plan`, {
                      projectId: currentProject!.id,
                    }),
                    icon: `plans-and-billing`,
                  },
                ]
              : []),
            ...(showInviteUsers
              ? [
                  {
                    label: i18n.t(`settings.invite-users`),
                    value: FooterMenuItemType.InviteUsers,
                    icon: `team`,
                    onSelect: () => globalSidenavController.openInviteUserModal(),
                  },
                ]
              : []),
            ...(showBackToSignup
              ? [
                  {
                    label: i18n.t(`settings.back-to-signup`),
                    value: FooterMenuItemType.BackToSignup,
                    icon: `arrow-left`,
                    href: `/find-your-org`,
                  },
                ]
              : []),
            ...(startupCreditPromoSectionItem ? [startupCreditPromoSectionItem] : []),
          ],
        },
        ...(adminOrgs.length > 0
          ? [
              {
                label: i18n.t(`settings.admin-org-menu`),
                items: adminOrgs.map((org) => ({
                  label: org.name,
                  href: `/organization/${org.id}/app/settings/overview`,
                })),
              },
            ]
          : []),
        {
          items: [
            {
              label: currentUser?.isGuest ? i18n.t(`settings.leave-demo`) : i18n.t(`settings.logout`),
              icon: `arrow-down`,
              onSelect: () => logout({localStorageManager}),
            },
          ],
        },
      ]}
    />
  );
}

function HelpMenu(props: {
  productUpdates: ProductUpdate[];
  setShowChangeModal: (value: boolean) => void;
  onMenuChange: (ev: MenuChangeEvent) => void;
}) {
  const currentProject = useCurrentProject().get().value;
  const currentUser = useCurrentUser().get().value;
  const supportInfo = useSupportInfo().get().value;
  const i18n = useTranslation();
  const globalSidenavController = useGlobalSidenavController();

  const getHelpDocsLink = () => {
    const reportName = getReportName();
    switch (reportName) {
      case ReportName.Dashboards:
        return `https://docs.mixpanel.com/docs/boards/overview`;
      case ReportName.Insights:
      case ReportName.Funnels:
      case ReportName.Retention:
      case ReportName.Flows:
        return `https://docs.mixpanel.com/docs/reports/${reportName}`;
      case ReportName.Impact:
        return `https://docs.mixpanel.com/docs/reports/apps/impact`;
      case ReportName.Signal:
        return `https://docs.mixpanel.com/docs/reports/apps/signal`;
      case ReportName.ExperimentReporting:
        return `https://docs.mixpanel.com/docs/reports/apps/experiments`;
      case ReportName.Settings:
        return `https://docs.mixpanel.com/docs/orgs-and-projects/overview`;
      case ReportName.Lexicon:
        return `https://docs.mixpanel.com/docs/data-governance/lexicon`;
      default:
        return `https://docs.mixpanel.com`;
    }
  };

  const projectExternalLinks = currentProject?.externalHelpLinks ?? [];
  const unseenProductUpdates = getUnseenProductUpdates(props.productUpdates);

  return (
    <FooterMenu
      type={FooterMenuType.Help}
      className={clsx(styles.helpSelect, {[styles.notifDot]: unseenProductUpdates.length > 0})}
      icon={`brand-help`}
      onMenuChange={props.onMenuChange}
      sections={[
        {
          items: [
            {
              label: i18n.t(`help.documentation`),
              value: FooterMenuItemType.Documentation,
              icon: `documentation`,
              href: getHelpDocsLink(),
              target: `_blank`,
            },
            {
              label: i18n.t(`help.changelog`),
              value: FooterMenuItemType.Changelog,
              ...(unseenProductUpdates.length > 0 && {sideLabel: unseenProductUpdates.length.toString()}),
              icon: `changelog`,
              onSelect: () => {
                mixpanel.track(`[Changelog] In-product changelog modal opened`);
                props.setShowChangeModal(true);
              },
              href: props.productUpdates ? null : `https://docs.mixpanel.com/changelogs`,
              target: `_blank`,
            },
            {
              label: i18n.t(`help.demo-projects`),
              value: FooterMenuItemType.DemoProjects,
              icon: `open`,
              onSelect: () =>
                openDemoProjectSelectorModal((openDemoProjectEvent) => {
                  addToast({message: openDemoProjectEvent.detail.message, type: ToastType.ERROR});
                }),
            },
            {
              label: i18n.t(`help.submit-feedback`),
              value: FooterMenuItemType.SubmitFeedback,
              icon: `sent`,
              onSelect: () =>
                promptFeedbackModal({
                  feature: FeedbackForFeature.General,
                }),
            },
            ...(supportInfo?.supportChatEnabled
              ? [
                  {
                    label: i18n.t(`help.live-chat`),
                    value: FooterMenuItemType.LiveChat,
                    icon: `user`,
                    onSelect: () => globalSidenavController.openZendeskChat(),
                  },
                ]
              : []),
            ...(!currentUser?.isGuest
              ? [
                  {
                    label: i18n.t(`help.contact-support`),
                    value: FooterMenuItemType.ContactSupport,
                    icon: `help-center`,
                    onSelect: () => globalSidenavController.openContactSupport(),
                  },
                ]
              : []),
            {
              label: i18n.t(`help.contact-sales`),
              value: FooterMenuItemType.ContactSales,
              icon: `email`,
              href: `https://mixpanel.com/contact-us/sales/`,
              target: `_blank`,
            },
          ],
        },
        // some projects have links pointing to slack channels, or google docs, these will
        // render at the bottom of the list
        ...(projectExternalLinks
          ? [
              {
                items: projectExternalLinks.map((link) => ({
                  label: link.linkName,
                  value: FooterMenuItemType.ExternalLink,
                  href: link.linkURL,
                  target: `_blank`,
                  icon: `slack-logo`,
                  isDisabled: !link.linkName,
                })),
              },
            ]
          : []),
      ]}
    />
  );
}

function ApplicationMenu() {
  const currentProject = useCurrentProject().get().value;
  const globalSidenavController = useGlobalSidenavController();

  if (!currentProject) {
    return null;
  }

  const isProjectOrGlobalWorkspaceVal = currentProject ? isProjectOrGlobalWorkspace({currentProject}) : undefined;

  const footerReportInfo: Record<FooterAppReportName, FooterAppReportInfo> = {
    [ReportName.JqlConsole]: {
      label: ({t}) => t(`mixology.app.jql`),
      endpoint: `jql-console`,
      // JQL would allow users to bypass data filters, it should be hidden for workspaces
      hidden: currentProject?.hasFeatureFlag(ProjectFeatureFlag.HideReportJqlConsole),
      disabling: {
        isDisabled: !isProjectOrGlobalWorkspaceVal,
        disabledMsg: ({t}) => t(`nav-header.jql-disabled`),
      },
    },
    [ReportName.Signal]: {
      label: ({t}) => t(`mixology.app.signal`),
      endpoint: `signal`,
    },
    [ReportName.Impact]: {
      label: ({t}) => t(`mixology.app.impact`),
      endpoint: `impact`,
    },
    [ReportName.ExperimentReporting]: {
      label: ({t}) => t(`mixology.app.experiments`),
      endpoint: `experiment-reporting`,
      hidden: [
        UNIQUE_PROJECTS.Uber, // Uber
        UNIQUE_PROJECTS.UberEats, // Uber-Eats
      ].includes(currentProject?.id.toString()),
    },
  };

  const isDemoProject = currentProject?.isDemo ?? true;
  const footerAppReportNames: FooterAppReportName[] = [
    ReportName.Signal,
    ReportName.Impact,
    ReportName.ExperimentReporting,
    ...(!isDemoProject ? ([ReportName.JqlConsole] as const) : []),
  ];

  const applicationSectionItems: SectionItem[] = footerAppReportNames
    .filter((report) => !footerReportInfo[report].hidden)
    .map((report): SectionItem => {
      const objReport = footerReportInfo[report];
      const isDisabled = Boolean(objReport.disabling?.isDisabled);
      const url = globalSidenavController.getWorkspaceBasedAppUrl(objReport.endpoint);

      return {
        label: objReport.label,
        value: report,
        href: url,
        isDisabled,
        tooltip: isDisabled
          ? {
              placement: Placement.Right,
              text: objReport.disabling!.disabledMsg,
            }
          : undefined,
      };
    });

  return (
    <FooterMenu
      type={FooterMenuType.OverflowApps}
      icon={`brand-applications`}
      sections={[
        {
          items: applicationSectionItems,
        },
      ]}
    />
  );
}

function SupportWidget() {
  const supportInfo = useSupportInfo().get().value;
  const currentProject = useCurrentProject().get().value;
  const currentUser = useCurrentUser().get().value || userCookieUtils.getUserIdentityFromCookie();
  const globalSidenavController = useGlobalSidenavController();

  if (!globalSidenavController.state.showSupportWidget || !supportInfo || !currentProject || !currentUser) {
    return null;
  }

  return (
    <SupportWidgetReact
      className={styles.supportWidget}
      eventsPlanId={currentProject?.eventsPlanID}
      peoplePlanId={currentProject?.peoplePlanID}
      billingAccount={supportInfo.billingAccount}
      supportPlan={supportInfo.supportPlan}
      userAgeInDays={supportInfo.userAgeDays}
      userEmail={currentUser.email}
      userId={currentUser.id}
      userName={currentUser.name}
      projectId={currentProject.id}
      shouldOverrideFreeWarning={currentProject.hasFeatureFlag(ProjectFeatureFlag.FreeSupportTrial)}
      onCloseWidget={() => globalSidenavController.updateState({showSupportWidget: false})}
      maximize={globalSidenavController.state.isSupportWidgetMaximized}
    />
  );
}

function GlobalSidenavFooterContent(props: Props & {userIdentity: UserIdentity}) {
  const globalSidenavController = useGlobalSidenavController();
  const currentProject = useCurrentProject().get().value;

  const [productUpdates, setProductUpdates] = useState<ProductUpdate[]>([]);
  const [showChangeModal, setShowChangeModal] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(props.location.search);
    if (searchParams.has(`contact-us`) && searchParams.get(`contact-us`) !== `false`) {
      globalSidenavController.openContactSupport({isSupportWidgetMaximized: true});
    }
  }, [props.location]);

  useEffect(() => {
    const fetchProductUpdates = async () => {
      const productUpdatesData = await getProductUpdates();
      setProductUpdates(productUpdatesData);
    };
    fetchProductUpdates();
  }, [setProductUpdates]);

  const handleHelpMenuChange = (ev: MenuChangeEvent) => {
    if (!ev.detail.isOpen) {
      const unseen = getUnseenProductUpdates(productUpdates);
      if (unseen.length > 0) {
        markProductUpdatesAsSeen(unseen.map(({id}) => id));
        setProductUpdates(productUpdates.map((update) => ({...update, seen: true})));
      }
    }
  };

  return (
    <div
      className={clsx(styles.globalSidenavFooter, {[styles.collapsedSidenav]: globalSidenavController.isCollapsed()})}
    >
      {currentProject && <ApplicationMenu />}
      <HelpMenu
        productUpdates={productUpdates}
        setShowChangeModal={setShowChangeModal}
        onMenuChange={handleHelpMenuChange}
      />
      <SettingsMenu />
      {showChangeModal && (
        <ChangeModalReact onCloseHandler={() => setShowChangeModal(false)} productUpdates={productUpdates ?? []} />
      )}
      <SupportWidget />
    </div>
  );
}

export const GlobalSidenavFooter = withStyles((props: Props) => {
  const userIdentity = userCookieUtils.getUserIdentityFromCookie();
  if (!userIdentity) {
    return null;
  }
  return <GlobalSidenavFooterContent {...props} userIdentity={userIdentity} />;
}, css);
